import React, { Component, ErrorInfo, ReactNode } from 'react';
import logger from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🛡️ ErrorBoundary: Uncaught error:', error, errorInfo);
    
    // Log error details
    logger.error('ErrorBoundary caught error:', {
      component: this.props.componentName || 'Unknown',
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div style={{
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          padding: '24px',
          margin: '16px 0',
          textAlign: 'center',
          fontFamily: 'Gilroy, sans-serif'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#dc2626', margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700' }}>
            Something went wrong
          </h3>
          <p style={{ color: '#7f1d1d', margin: '0 0 16px 0', fontSize: '14px' }}>
            {this.props.componentName ? `Error in ${this.props.componentName}` : 'An unexpected error occurred'}
          </p>
          
          {/* Development mode: Show error details */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ 
              textAlign: 'left', 
              background: '#fff1f2', 
              padding: '12px', 
              borderRadius: '6px',
              border: '1px solid #fecaca',
              marginTop: '16px'
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#dc2626' }}>
                🔍 Error Details (Development)
              </summary>
              <pre style={{ 
                fontSize: '12px', 
                color: '#7f1d1d', 
                marginTop: '12px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {this.state.error.message}
              </pre>
            </details>
          )}
          
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '16px'
            }}
          >
            🔄 Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;